export interface PdfRequestData {
  template: Template;
  pentest: Pentest;
  vulnerabilities: Vulnerability[];
  appendix: AppendixFile[];
}

export const confidentialityDefault =
  "# Confidentiality\nThis report contains sensitive and confidential information. It is essential to take precautions to maintain the confidentiality of the content within this document. Unauthorized publication of this report could harm the reputation of {{ client }} or aid in facilitating attacks against {{ client }}. Pentra Solutions will not be responsible for any special, incidental, collateral, or consequential damages resulting from the use of this information.\n# Disclaimer\nPlease note that this assessment may not reveal all existing vulnerabilities within the systems examined. This report summarizes findings from a \"point-in-time\" evaluation of {{ client }}'s environment. Any modifications made to the environment during the testing period may impact the assessment results.";

export const executiveSummaryDefault =
  "At the request of {{ client }}, we have prepared this executive summary to provide a comprehensive overview of our findings and recommendations based on our recent analysis and assessment. This summary aims to present the key insights and strategic recommendations that will assist {{ client }} in further strenghtening its defense.\n# Assessment Details\n{{ company }} conducted a security assessment of the internal corporate network of {{ client }} during {{ start-date }} - {{ end-date }}. This penetration test, performed by {{ company }}, simulated an attack by an {{ type }} threat actor aiming to access systems within {{ client }}'s corporate network.\n# Key Findings\nThe primary objective of this assessment was to identify vulnerabilities in {{ client }}'s infrastructure and recommend remediation strategies. {{ company }} discovered a total of {{ vulnerabilities-count }} vulnerabilities, categorized by severity in the following graph.";

export const methodologiesDefault =
  "# Assessment Approach\nThe security assessment by {{ company }} followed three phases: Reconnaissance, Target Assessment, and Vulnerability Exploitation. During reconnaissance, comprehensive information about {{ client }}'s network was gathered using techniques like network scanning, fuzzing, and banner grabbing. {{ company }} identified potential targets and assessed their significance. In the Vulnerability Exploitation phase, simulated attacks uncovered network vulnerabilities. Findings were meticulously documented to minimize business disruption.\n# Vulnerabilities Classification\nDiscovered vulnerabilities are classified based on impact and ease of exploitation. The following table outlines our classification system:";

export interface Font {
  Name: string;
  Regular: string;
  Bold?: string;
  Italic?: string;
  BoldItalic?: string;
}

export interface ContactInformation {
  PhoneNumber: string;
  Name: string;
  Company: string;
  Email: string;
}

export type AlignmentType = "Left" | "Center" | "Right" | "None";
export type ChartType = "Bar" | "Pie" | "Donut";
export type RoleType = "owner" | "viewer" | "editor";

export type TeamMember = {
  email: string,
  name: string,
  role: RoleType,
  user_id: number,
  avatarUrl?: string;
};

export interface CustomField {
  Name: string;
  Value: string;
}

export interface Template {
  ID: number;
  TemplateName: string;

  // Colors
  PrimaryColor: string;
  SecondaryColor: string;
  BackgroundColor: string;

  // Fonts
  TextFont: Font;
  TitleFont: Font;
  TextFontSize: number;
  TitleFontSize: number;

  // Elements
  ExecutiveSummary: string;
  Confidentiality: string;
  Methodology: string;
  Recommendations: string;
  Motto: string;
  Logo: string;
  ChartType: ChartType;
  FooterText: string;

  // Layout
  LogoPosition: AlignmentType;
  MottoPosition: AlignmentType;
  PageNumberPosition: AlignmentType;

  ContactInformation: ContactInformation;
  PageOrder: Page[];

  // DOCX Template
  DocxPath?: string;
  FileType?: "pdf" | "docx";

  // Custom Fields
  CustomDocxFields?: CustomField[];
}

export interface AppendixFile {
  ID: number;
  Template: number;
  OriginalName: string;
}

export interface Page {
  Name: string;
  Position: number;
  Hidden: boolean;

  Section?: string;
}

export type CategoryType = "blackbox" | "graybox" | "whitebox";
export type TypeType = "external" | "internal";

export interface Pentest {
  readonly ID: number;
  Token?: string;

  Title: string;
  StartDate: string;
  EndDate: string;

  Client: string;
  Type: TypeType;
  Category: CategoryType;
  Scope: string;
}

// Struct used when updating pentest
export interface ChangePentest {
  id: number;

  title?: string;
  startDate?: string;
  endDate?: string;
  category?: CategoryType;
  type?: TypeType;
  client?: string;
  scope?: string;
}

export interface Vulnerability {
  readonly ID: number;
  Pentest: number;
  Name: string;
  Description: string;
  Severity: string;
  Poc: string;
  Countermeasures: string;
  Tag?: string;
}

export interface ChangeVuln {
  id: number;
  name?: string;
  description?: string;
  severity?: string;
  poc?: string;
  countermeasures?: string;
}
export interface CreateVuln {
  Name: string;
  Pentest: number;
}

export interface IOBlock {
  type: "command" | "request";
  input: string;
  output: string;
  timestamp: number;
  id: number;
}

export interface Command {
  readonly ID: number;
  Command: string;
  Output: string;

  ModelResponse: string;
  Pentest: number;
  Timestamp: number;
}

export interface NetworkRequest {
  readonly ID: number;
  Request: string;
  Response: string;

  ModelResponse: string;
  Pentest: number;
  Timestamp: number;
}

export interface CommandConnection {
  vulnerability_id: number;
  command_id: number;
}
export interface NetworkRequestConnection {
  vulnerability_id: number;
  network_request_id: number;
}

export const emptyPentest: Pentest = {
  ID: 0,
  Token: "",

  Title: "",
  StartDate: "",
  EndDate: "",

  Client: "",
  Type: "external",
  Category: "graybox",
  Scope: "",
};

export const emptyContactInformation = {
  Name: "",
  PhoneNumber: "",
  Company: "",
  Email: "",
};

export const defaultPageOrder: Page[] = [
  { Position: 1, Name: "Cover Page", Hidden: false },
  { Position: 2, Name: "Table of Contents", Hidden: false },
  { Position: 3, Name: "Confidentiality", Hidden: false, Section: confidentialityDefault },
  { Position: 4, Name: "Executive Summary", Hidden: false, Section: executiveSummaryDefault },
  { Position: 5, Name: "Methodologies", Hidden: false, Section: methodologiesDefault },
  { Position: 6, Name: "Vulnerabilities", Hidden: false },
];

export const defaultTemplate: Template = {
  ID: 0,
  TemplateName: "",

  // Colors
  PrimaryColor: "#07d3fa",
  SecondaryColor: "#0e6ba8",
  BackgroundColor: "#fafafa",

  // Fonts
  TextFont: {
    Name: "Open Sans",
    Regular: "https://fonts.gstatic.com/s/opensans/v40/memSYaGs126MiZpBA-UvWbX2vVnXBbObj2OVZyOOSr4dVJWUgsjZ0C4nY1M2xLER.ttf",
    Bold: "https://fonts.gstatic.com/s/opensans/v40/memSYaGs126MiZpBA-UvWbX2vVnXBbObj2OVZyOOSr4dVJWUgsg-1y4nY1M2xLER.ttf",
    Italic: "https://fonts.gstatic.com/s/opensans/v40/memQYaGs126MiZpBA-UFUIcVXSCEkx2cmqvXlWq8tWZ0Pw86hd0Rk8ZkaVcUwaERZjA.ttf",
    BoldItalic: "https://fonts.gstatic.com/s/opensans/v40/memQYaGs126MiZpBA-UFUIcVXSCEkx2cmqvXlWq8tWZ0Pw86hd0RkyFjaVcUwaERZjA.ttf"
  },
  TitleFont: {
    Name: "Open Sans",
    Regular: "https://fonts.gstatic.com/s/opensans/v40/memSYaGs126MiZpBA-UvWbX2vVnXBbObj2OVZyOOSr4dVJWUgsjZ0C4nY1M2xLER.ttf",
    Bold: "https://fonts.gstatic.com/s/opensans/v40/memSYaGs126MiZpBA-UvWbX2vVnXBbObj2OVZyOOSr4dVJWUgsg-1y4nY1M2xLER.ttf",
    Italic: "https://fonts.gstatic.com/s/opensans/v40/memQYaGs126MiZpBA-UFUIcVXSCEkx2cmqvXlWq8tWZ0Pw86hd0Rk8ZkaVcUwaERZjA.ttf",
    BoldItalic: "https://fonts.gstatic.com/s/opensans/v40/memQYaGs126MiZpBA-UFUIcVXSCEkx2cmqvXlWq8tWZ0Pw86hd0RkyFjaVcUwaERZjA.ttf"
  },
  TextFontSize: 12,
  TitleFontSize: 35,

  ExecutiveSummary: "",
  Confidentiality: "",
  Methodology: "",
  Recommendations: "",
  // Elements
  Motto: "",
  Logo: "",
  ChartType: "Pie",
  FooterText:
    "Copyright 2023 SecureCorp Cybersecurity Services. All rights reserved. This document is confidential and intended for authorized personnel only.",

  // Layout
  LogoPosition: "Left",
  MottoPosition: "Right",
  PageNumberPosition: "None",

  ContactInformation: emptyContactInformation,

  PageOrder: defaultPageOrder,

  // DOCX Template defaults
  DocxPath: "",
  FileType: "pdf",

  CustomDocxFields: [],
};

export const emptyPage: Page = {
  Name: "",
  Position: 0,
  Hidden: true,

  Section: "",
};
